import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { getSupabase } from '@/lib/supabase';
import { useAllUnits } from '@/hooks/useUnits';
import { errorMessageOf } from '@/lib/billingErrors';

/**
 * Shown when the host picks a plan with fewer units than they have ACTIVE.
 *
 * The server refuses such a change (TOO_MANY_UNITS) — so before the change is
 * made (now or scheduled for the renewal) the host deactivates exactly the
 * surplus: never more than needed to fit the new cap. Deactivated units keep
 * everything (bookings, settings) and don't take new bookings; they can be
 * switched back on within the plan's cap.
 *
 * Properties are not counted separately — the cap is on units.
 *
 * UnitSurplusPicker / setUnitsActive are shared with PlanChangeFlow, which
 * shows the same list INSIDE its confirmation and deactivates only when the
 * host confirms (and switches the units back on if the change then fails).
 */

/**
 * Switch units on/off in one request; refreshes lists, calendars and plan
 * numbers. Returns the ids that REALLY changed (RLS / a deleted unit can make
 * the update touch fewer rows than asked).
 */
export async function setUnitsActive(ids: string[], active: boolean, queryClient?: QueryClient): Promise<string[]> {
  if (ids.length === 0) return [];
  const { data, error } = await getSupabase()
    .from('units')
    .update({ is_active: active, updated_at: new Date().toISOString() })
    .in('id', ids)
    .is('deleted_at', null) // a unit in the trash is not switched on/off
    .select('id');
  if (error) throw error;
  if (queryClient) {
    queryClient.invalidateQueries({ queryKey: ['units'] });
    queryClient.invalidateQueries({ queryKey: ['calendar'] });
    queryClient.invalidateQueries({ queryKey: ['subscription'] });
  }
  return ((data ?? []) as Array<{ id: string }>).map((row) => row.id);
}

/** Which of these units still exist for the host (not deleted, not in the trash). */
export async function existingUnitIds(ids: string[]): Promise<string[]> {
  if (ids.length === 0) return [];
  const { data, error } = await getSupabase()
    .from('units')
    .select('id')
    .in('id', ids)
    .is('deleted_at', null);
  if (error) throw error;
  return ((data ?? []) as Array<{ id: string }>).map((row) => row.id);
}

/** Active units of the host (what the plan cap counts) + whether they loaded. */
export function useActiveUnits() {
  const query = useAllUnits();
  const units = useMemo(() => (query.data ?? []).filter((u) => u.is_active), [query.data]);
  return {
    units,
    /** Still loading — nothing to pick yet. */
    isLoading: !!query.isPending && query.data === undefined,
    /** Loading failed — offer a retry, never an empty list. */
    isError: !!query.isError && query.data === undefined,
    /** A (re)fetch is running. */
    isFetching: !!query.isFetching,
    /** The list was fetched after this component mounted (not only cached). */
    isFetchedAfterMount: query.isFetchedAfterMount !== false,
    refetch: () => {
      void query.refetch?.();
    },
  };
}

/** Spinner / error + retry while the units are not there yet. */
export function UnitsLoadState({ isLoading, isError, onRetry }: { isLoading: boolean; isError: boolean; onRetry: () => void }) {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]" data-testid="units-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('subscription.downgrade.loadingUnits', 'Učitavam jedinice…')}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm" role="alert" data-testid="units-error">
        <span className="flex-1 text-red-600 dark:text-red-400">
          {t('subscription.downgrade.unitsFailed', 'Popis jedinica se nije učitao.')}
        </span>
        <Button size="sm" variant="outline" onClick={onRetry}>
          {t('subscription.required.retry', 'Pokušaj ponovno')}
        </Button>
      </div>
    );
  }
  return null;
}

interface UnitSurplusPickerProps {
  units: Array<{ id: string; name: string; property_name?: string }>;
  /** Exactly this many must be (and can be) ticked. */
  toRemove: number;
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}

/** Checklist that lets the host tick EXACTLY the surplus — never more. */
export function UnitSurplusPicker({ units, toRemove, selected, onChange }: UnitSurplusPickerProps) {
  const { t } = useTranslation();
  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else if (next.size < toRemove) next.add(id);
    onChange(next);
  };
  return (
    <section>
      <p className="text-sm mb-2">
        {t('subscription.downgrade.needToRemove', { current: units.length, count: toRemove })}
      </p>
      {selected.size < toRemove && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
          {t('subscription.downgrade.selectAtLeast', { count: toRemove })}
        </p>
      )}
      <ul className="space-y-2 border border-[hsl(var(--border))] rounded-lg p-3">
        {units.map((u) => {
          const checked = selected.has(u.id);
          const disabled = !checked && selected.size >= toRemove;
          return (
            <li key={u.id}>
              <label className={`flex items-center gap-3 py-1 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  data-testid={`unit-checkbox-${u.id}`}
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggle(u.id)}
                  className="h-5 w-5"
                />
                <span className="flex-1 text-sm">
                  {u.name}
                  {u.property_name && <span className="text-[hsl(var(--muted-foreground))]"> · {u.property_name}</span>}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

interface DowngradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Plan name shown in the heading, e.g. "Starter". */
  newPlanName: string;
  /** Maximum active units allowed by the new plan. */
  newMaxUnits: number;
  /** Kept for old callers; properties are not limited separately. */
  newMaxProperties?: number;
  /** Called after the deactivation succeeded; the caller continues with the plan change. */
  onConfirmDowngrade: () => void | Promise<void>;
  /**
   * Call onClose after a successful confirm (default). A caller that moves on
   * to its next step itself (PlanChangeFlow) passes false — onClose there
   * means "the host cancelled".
   */
  closeAfterConfirm?: boolean;
}

export function DowngradeModal({
  isOpen,
  onClose,
  newPlanName,
  newMaxUnits,
  onConfirmDowngrade,
  closeAfterConfirm = true,
}: DowngradeModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedUnits, setSelectedUnits] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { units: activeUnits, isLoading: unitsLoading, isError: unitsError, refetch: refetchUnits } = useActiveUnits();
  const unitsReady = !unitsLoading && !unitsError;
  const unitsToRemove = Math.max(0, activeUnits.length - newMaxUnits);
  const enoughSelected = selectedUnits.size >= unitsToRemove;
  const canSubmit = unitsReady && enoughSelected && !submitting;

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await setUnitsActive(Array.from(selectedUnits), false, queryClient);

      await onConfirmDowngrade();
      if (closeAfterConfirm) onClose();
    } catch (e) {
      console.error('Downgrade deactivation failed:', errorMessageOf(e));
      setError(t('subscription.downgrade.failed', 'Deaktivacija nije uspjela. Ništa nije promijenjeno na pretplati — pokušaj ponovno.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="downgrade-modal"
      className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50"
    >
      <div className="bg-[hsl(var(--background))] rounded-t-2xl sm:rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-[hsl(var(--border))]">
          <div>
            <h2 className="text-lg font-semibold">{t('subscription.downgrade.title')}</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              {t('subscription.downgrade.subtitle', {
                plan: newPlanName,
                maxUnits: newMaxUnits,
                current: activeUnits.length,
              })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-[hsl(var(--muted))]"
            aria-label={t('common.close', 'Zatvori')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-5">
          {!unitsReady ? (
            <UnitsLoadState isLoading={unitsLoading} isError={unitsError} onRetry={refetchUnits} />
          ) : unitsToRemove > 0 ? (
            <UnitSurplusPicker
              units={activeUnits}
              toRemove={unitsToRemove}
              selected={selectedUnits}
              onChange={setSelectedUnits}
            />
          ) : (
            <p className="text-sm">
              {t('subscription.downgrade.nothingToRemove', 'Tvoje aktivne jedinice stanu u novi plan — ništa ne treba deaktivirati.')}
            </p>
          )}

          <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg text-sm">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <span className="text-amber-800 dark:text-amber-200">{t('subscription.downgrade.confirmBody')}</span>
          </div>

          {error && (
            <div role="alert" className="p-3 bg-[hsl(var(--destructive))]/10 border border-[hsl(var(--destructive))]/20 rounded-md text-sm text-[hsl(var(--destructive))]">
              {error}
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 p-5 border-t border-[hsl(var(--border))]">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('subscription.downgrade.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={!canSubmit}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {unitsToRemove > 0
              ? t('subscription.downgrade.deactivate')
              : t('subscription.downgrade.continue', 'Nastavi')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default DowngradeModal;
